import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Idle = no mouse/keyboard/touch/scroll for IDLE_TIMEOUT_MS. We warn
// WARN_BEFORE_MS before the logout fires so the user can save their work.
// Values chosen for a reasonable café-leave-the-laptop-open scenario.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;    // 30 min total
const WARN_BEFORE_MS = 60 * 1000;          // show banner at T-60s
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"];

export default function IdleLogoutTimer() {
  const { user, logout } = useAuth();
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const lastActivity = useRef(Date.now());
  const warnedAt = useRef(0);

  // Reset the idle clock on any user input. Throttled via requestAnimationFrame
  // so a fast mousemove stream doesn't thrash setState — we only touch the
  // ref, and the interval below checks it.
  useEffect(() => {
    if (!user) return;
    const bump = () => {
      lastActivity.current = Date.now();
      if (warning) {
        setWarning(false);
        warnedAt.current = 0;
      }
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, bump));
  }, [user, warning]);

  // Tick once a second checking the elapsed-since-activity. Low-cost — a
  // single setInterval, no per-event work.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_TIMEOUT_MS) {
        clearInterval(id);
        setWarning(false);
        logout();
        toast.error("You were signed out after 30 minutes of inactivity.");
        return;
      }
      if (idle >= IDLE_TIMEOUT_MS - WARN_BEFORE_MS) {
        if (!warnedAt.current) warnedAt.current = Date.now();
        setWarning(true);
        setRemaining(Math.max(0, Math.ceil((IDLE_TIMEOUT_MS - idle) / 1000)));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [user, logout]);

  const stayActive = () => {
    lastActivity.current = Date.now();
    setWarning(false);
    warnedAt.current = 0;
    toast.success("Welcome back — session kept active.");
  };

  if (!user || !warning) return null;
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      data-testid="idle-logout-banner"
      className="fixed inset-x-0 bottom-0 z-[60] bg-neutral-900 text-neutral-50 border-t border-amber-500"
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 text-sm">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          <div>
            <div className="font-semibold">Are you still there?</div>
            <div className="text-neutral-300">
              Signing out in <span className="font-mono tabular-nums font-bold text-amber-300" data-testid="idle-countdown">{mm}:{ss}</span>
              {" "}due to inactivity. Save any unsaved work.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          <button
            onClick={stayActive}
            data-testid="idle-stay"
            className="bg-amber-500 hover:bg-amber-400 text-neutral-900 text-sm font-semibold px-4 py-2"
          >
            I'm still here
          </button>
          <button
            onClick={logout}
            data-testid="idle-signout"
            className="text-sm underline underline-offset-2 text-neutral-300 hover:text-white"
          >
            Sign out now
          </button>
        </div>
      </div>
    </div>
  );
}
